import React from 'react';
import { PaginationButton } from '../../atoms';
import { PaginationControls } from '../../molecules';

/**
 * Pagination Organism Component
 * Complete pagination component with page numbers and navigation
 */
const Pagination = ({ 
  currentPage = 1,
  totalPages = 1,
  onPageChange,
  maxVisiblePages = 5,
  className = '',
  ...props 
}) => {
  if (totalPages <= 1) {
    return null;
  }

  const handlePageClick = (page) => {
    if (page !== currentPage && page >= 1 && page <= totalPages) {
      onPageChange(page);
    }
  };

  const handlePrevious = () => {
    if (currentPage > 1) {
      handlePageClick(currentPage - 1);
    }
  };

  const handleNext = () => {
    if (currentPage < totalPages) {
      handlePageClick(currentPage + 1);
    }
  };

  // Calculate which page numbers to show
  const getVisiblePages = () => {
    const pages = [];
    const halfVisible = Math.floor(maxVisiblePages / 2);
    
    let startPage = Math.max(1, currentPage - halfVisible);
    let endPage = Math.min(totalPages, currentPage + halfVisible);

    // Adjust if we're near the start or end
    if (endPage - startPage < maxVisiblePages - 1) {
      if (startPage === 1) {
        endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
      } else {
        startPage = Math.max(1, endPage - maxVisiblePages + 1);
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return pages;
  };

  const visiblePages = getVisiblePages();
  const showFirstPage = visiblePages[0] > 1;
  const showLastPage = visiblePages[visiblePages.length - 1] < totalPages;

  return (
    <div className={`flex flex-col items-center gap-4 ${className}`} {...props}>
      {/* Page Info */}
      <div className="text-sm text-gray-600">
        Page {currentPage} of {totalPages}
      </div>

      {/* Pagination Controls */}
      <div className="flex items-center gap-2">
        <PaginationControls
          onPrevious={handlePrevious}
          onNext={handleNext}
          hasPrevious={currentPage > 1}
          hasNext={currentPage < totalPages}
        />

        {/* Page Numbers */}
        <div className="flex items-center gap-2">
          {showFirstPage && (
            <>
              <PaginationButton
                page={1}
                onClick={() => handlePageClick(1)}
                isActive={currentPage === 1}
              />
              {visiblePages[0] > 2 && (
                <span className="px-2 text-gray-500">...</span>
              )}
            </>
          )}

          {visiblePages.map((page) => (
            <PaginationButton
              key={page}
              page={page}
              onClick={() => handlePageClick(page)}
              isActive={currentPage === page}
            />
          ))}

          {showLastPage && (
            <>
              {visiblePages[visiblePages.length - 1] < totalPages - 1 && (
                <span className="px-2 text-gray-500">...</span>
              )}
              <PaginationButton
                page={totalPages}
                onClick={() => handlePageClick(totalPages)}
                isActive={currentPage === totalPages}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Pagination;

